import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

export interface FeedTab<T extends string = string> {
  id: T;
  label: string;
  /** Red count after the label; 0 = no badge. */
  badge: number;
}

/**
 * Tab strip inside a feed page's main card (GTFS and SIRI). Same look as the
 * provider shell's Feeds | Details tabs (`ProviderShellLayoutComponent`):
 * uppercase 13px labels, ink-1 active / ink-3 inactive, a 2px action-colour
 * underline on the active tab, plus an optional red error-count badge.
 * Purely presentational — the page owns the active tab.
 */
@Component({
  selector: 'app-feed-tab-strip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex items-stretch gap-1 border-b border-hairline px-3', role: 'tablist' },
  template: `
    @for (tab of tabs(); track tab.id) {
      <button
        type="button"
        role="tab"
        [attr.aria-selected]="tab.id === active()"
        (click)="selected.emit(tab.id)"
        class="relative flex items-center gap-2 border-none bg-transparent px-4 py-3.5"
      >
        <span
          class="text-[13px] font-bold uppercase tracking-wide"
          [class.text-ink-1]="tab.id === active()"
          [class.text-ink-3]="tab.id !== active()"
          >{{ tab.label }}</span
        >
        @if (tab.badge > 0) {
          <span class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1.5 text-[11px] font-semibold text-white">
            {{ tab.badge }}
          </span>
        }
        @if (tab.id === active()) {
          <div class="absolute inset-x-3 bottom-0 h-0.5 rounded bg-action"></div>
        }
      </button>
    }
  `,
})
export class FeedTabStripComponent {
  readonly tabs = input.required<FeedTab[]>();
  readonly active = input.required<string>();
  readonly selected = output<string>();
}
